import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { keys } from "../../lib/queryClient";
import type { CheckInComment, CheckInLike, ReactionKind } from "../../lib/database.types";

/**
 * Reactions (flame, heart, thumbs-up) and comments on check-ins.
 *
 * Four people, so both tables are fetched whole and grouped on the client. That
 * keeps the feed to two extra requests rather than one per card.
 */
export type Reaction = Pick<CheckInLike, "check_in_id" | "owner_id" | "kind">;

export function useLikes() {
  return useQuery({
    queryKey: keys.likes,
    queryFn: async (): Promise<Reaction[]> => {
      const { data, error } = await supabase
        .from("check_in_likes")
        .select("check_in_id, owner_id, kind");
      if (error) throw error;
      return data;
    },
  });
}

export function useComments() {
  return useQuery({
    queryKey: keys.comments,
    queryFn: async (): Promise<CheckInComment[]> => {
      const { data, error } = await supabase
        .from("check_in_comments")
        .select("*")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });
}

export function useToggleReaction(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      checkInId,
      kind,
      active,
    }: {
      checkInId: string;
      kind: ReactionKind;
      /** True when the reaction is currently on, so this call removes it. */
      active: boolean;
    }) => {
      const { error } = active
        ? await supabase
            .from("check_in_likes")
            .delete()
            .eq("check_in_id", checkInId)
            .eq("owner_id", userId)
            .eq("kind", kind)
        : await supabase
            .from("check_in_likes")
            .insert({ check_in_id: checkInId, owner_id: userId, kind });
      if (error) throw error;
    },

    // The chip fills before the network answers.
    onMutate: async ({ checkInId, kind, active }) => {
      await queryClient.cancelQueries({ queryKey: keys.likes });
      const previous = queryClient.getQueryData<Reaction[]>(keys.likes);
      queryClient.setQueryData<Reaction[]>(keys.likes, (reactions = []) =>
        active
          ? reactions.filter(
              (r) => !(r.check_in_id === checkInId && r.owner_id === userId && r.kind === kind),
            )
          : [...reactions, { check_in_id: checkInId, owner_id: userId, kind }],
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(keys.likes, context.previous);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: keys.likes }),
  });
}

export function useAddComment(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ checkInId, body }: { checkInId: string; body: string }) => {
      const { error } = await supabase
        .from("check_in_comments")
        .insert({ check_in_id: checkInId, owner_id: userId, body: body.trim() });
      if (error) throw error;
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: keys.comments }),
  });
}

export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase.from("check_in_comments").delete().eq("id", commentId);
      if (error) throw error;
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: keys.comments }),
  });
}
